import { CaregiverApiDto } from "@/model/Caregiver";
import { updateIfDefined } from "@/util/object-utils";
import { Instant, LocalDate } from "@js-joda/core";
import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ClinicalNote,
  CreateClinicalNote,
  toCreateClinicalNoteApiDto,
} from "../model/ClinicalNote";
import ClinicalProtocolType from "../model/ClinicalProtocolType";
import { toOrganization } from "../model/Organization";
import { OffboardingRequest, toDiabetesPatient, toHypertensionPatient } from "../model/Patient";
import {
  DiabetesFieldsApiDto,
  hasDiabetes,
  hasHypertension,
  HypertensionFieldsApiDto,
  PatientApiDto,
} from "../model/PatientApiDto";
import { QuerySelectOption } from "../util/query-utils";
import { ensure } from "../util/type-utils";
import {
  addClinicalNote,
  checkResetPasswordToken,
  CheckResetPasswordTokenParams,
  checkSignUpInvitation,
  CheckSignUpInvitationParams,
  getBloodGlucoseObservations,
  getBloodPressureObservations,
  getClinicalNotes,
  GetObservationsOptions,
  getOrganization,
  getPatient,
  getPatientsForOrganization,
  getPrescribingCaregivers,
  getSelfUser,
  logInWithEmailAndPassword,
  logOut,
  offboardPatient,
  resendInvitationEmail,
  resetPassword,
  sendForgotPasswordEmail,
  signUp,
  updateBloodGlucoseClinicalProtocolAndThresholds,
  updateBloodPressureClinicalProtocol,
  updateDeliveryDate,
} from "./requests";

export const useLogInMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: logInWithEmailAndPassword,
    onSuccess: () => queryClient.invalidateQueries(),
    onError: console.error,
  });
};

export const useLogOutMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => logOut(),
    onMutate: () => queryClient.cancelQueries(),
    onSuccess: () => {
      queryClient.clear();
      return undefined;
    },
    onError: console.error,
  });
};

export const useSelfUserQuery = () =>
  useQuery({
    queryKey: ["self-user"],
    queryFn: getSelfUser,
  });

const ensureSelfUser = (queryClient: QueryClient) =>
  queryClient.ensureQueryData<CaregiverApiDto>({ queryKey: ["self-user"] });

export const useCheckSignUpInvitationQuery = (params: CheckSignUpInvitationParams) =>
  useQuery({
    queryKey: ["check-invitation"],
    queryFn: async () => {
      await checkSignUpInvitation(params);
      return true; // react-query demands we return something to put in the cache
    },
    retry: 0,
    staleTime: 0,
    gcTime: 0,
  });

export const useSignUpMutation = () => {
  return useMutation({
    mutationFn: signUp,
    onError: console.error,
  });
};

export const useResendInvitationEmailMutation = () => {
  return useMutation({
    mutationFn: resendInvitationEmail,
    onError: console.error,
  });
};

export const useSendForgotPasswordEmailMutation = () => {
  return useMutation({
    mutationFn: sendForgotPasswordEmail,
    onError: console.error,
  });
};

export const useCheckResetPasswordTokenQuery = (params: CheckResetPasswordTokenParams) =>
  useQuery({
    queryKey: ["check-reset-password-token"],
    queryFn: async () => {
      await checkResetPasswordToken(params);
      return true; // react-query demands we return something to put in the cache
    },
    retry: 0,
    staleTime: 0,
    gcTime: 0,
  });

export const useResetPasswordMutation = () => {
  return useMutation({
    mutationFn: resetPassword,
    retry: 0,
    onError: console.error,
  });
};

type PatientsQueryOptions<TOutput> = QuerySelectOption<PatientApiDto[], TOutput[]>;

const usePatientsQuery = <T>({ select }: PatientsQueryOptions<T>) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["patients"],
    queryFn: async () => {
      const { organizationId } = await ensureSelfUser(queryClient);
      return await getPatientsForOrganization(organizationId);
    },
    refetchInterval: 30_000, // 30 seconds
    gcTime: 3_600_000, // 1 hour
    select,
  });
};

export const useHypertensionPatientsQuery = () =>
  usePatientsQuery({
    select: (patientDtos) => patientDtos.filter(hasHypertension).map(toHypertensionPatient),
  });

export const useDiabetesPatientsQuery = () =>
  usePatientsQuery({
    select: (patientDtos) => patientDtos.filter(hasDiabetes).map(toDiabetesPatient),
  });

export const useHypertensionPatientQuery = (patientId: string) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["patients", patientId],
    initialData: () => {
      const patients = queryClient.getQueryData<PatientApiDto[]>(["patients"]);
      const patient = patients?.find((patient) => patient.id === patientId);
      if (patient) {
        ensure(
          patient,
          hasHypertension,
          (patient) => `Patient ${patient.id} is not a hypertension patient`
        );
        return { ...patient, unreviewedReadingsUpToDate: false };
      } else {
        return undefined;
      }
    },
    queryFn: async () => {
      const { organizationId } = await ensureSelfUser(queryClient);
      const patientDto = await getPatient({ patientId, organizationId });
      ensure(
        patientDto,
        hasHypertension,
        (patient) => `Patient ${patient.id} is not a hypertension patient`
      );
      return { ...patientDto, unreviewedReadingsUpToDate: true };
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
    select: (patient) => toHypertensionPatient(patient),
  });
};

export const useDiabetesPatientQuery = (patientId: string) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["patients", patientId],
    initialData: () => {
      const patients = queryClient.getQueryData<PatientApiDto[]>(["patients"]);
      const patient = patients?.find((patient) => patient.id === patientId);
      if (patient) {
        ensure(
          patient,
          hasDiabetes,
          (patient) => `Patient ${patient.id} is not a diabetes patient`
        );
        return { ...patient, unreviewedReadingsUpToDate: false };
      } else {
        return undefined;
      }
    },
    queryFn: async () => {
      const { organizationId } = await ensureSelfUser(queryClient);
      const patientDto = await getPatient({ patientId, organizationId });
      ensure(
        patientDto,
        hasDiabetes,
        (patient) => `Patient ${patient.id} is not a diabetes patient`
      );
      return { ...patientDto, unreviewedReadingsUpToDate: true };
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
    select: (patient) => toDiabetesPatient(patient),
  });
};

export const usePrescribingCaregiversQuery = () => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["caregivers"],
    queryFn: async () => {
      const { organizationId } = await ensureSelfUser(queryClient);
      return await getPrescribingCaregivers({ organizationId });
    },
    refetchOnWindowFocus: true,
  });
};

export const useBloodPressureObservationsQuery = ({
  patientId,
  from,
  to,
}: Pick<GetObservationsOptions, "patientId" | "from" | "to">) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["blood-pressure-observations", patientId, from.toString(), to.toString()],
    queryFn: async () => {
      const { organizationId } = await ensureSelfUser(queryClient);
      return await getBloodPressureObservations({ patientId, organizationId, from, to });
    },
  });
};

export const useBloodGlucoseObservationsQuery = ({
  patientId,
  from,
  to,
}: Pick<GetObservationsOptions, "patientId" | "from" | "to">) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["blood-glucose-observations", patientId, from.toString(), to.toString()],
    queryFn: async () => {
      const { organizationId } = await ensureSelfUser(queryClient);
      return await getBloodGlucoseObservations({ patientId, organizationId, from, to });
    },
  });
};

type ClinicalNotesOptions = {
  patientId: string;
} & QuerySelectOption<ClinicalNote[]>;

const useClinicalNotesQuery = ({ patientId, ...otherOptions }: ClinicalNotesOptions) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["clinical-notes", patientId],
    queryFn: async () => {
      const { organizationId } = await ensureSelfUser(queryClient);
      return await getClinicalNotes({ patientId, organizationId });
    },
    ...otherOptions,
  });
};

export const useHypertensionClinicalNotesQuery = (patientId: string) =>
  useClinicalNotesQuery({
    patientId,
    select: (clinicalNotes) => clinicalNotes.filter(({ type }) => type === "hypertension"),
  });

export const useDiabetesClinicalNotesQuery = (patientId: string) =>
  useClinicalNotesQuery({
    patientId,
    select: (clinicalNotes) => clinicalNotes.filter(({ type }) => type === "diabetes"),
  });

export const useAddClinicalNoteMutation = ({ patientId }: { patientId: string }) => {
  const queryClient = useQueryClient();
  const patientsQueryKey = ["patients"];
  const patientQueryKey = ["patients", patientId];
  const clinicalNotesQueryKey = ["clinical-notes", patientId];

  return useMutation({
    mutationFn: async (clinicalNote: CreateClinicalNote) => {
      const { organizationId } = await ensureSelfUser(queryClient);
      await addClinicalNote({
        patientId,
        organizationId,
        note: toCreateClinicalNoteApiDto(clinicalNote),
      });
    },
    onMutate: async (clinicalNote) => {
      await queryClient.cancelQueries({ queryKey: patientsQueryKey });
      await queryClient.cancelQueries({ queryKey: patientQueryKey });
      const currentPatients = queryClient.getQueryData<PatientApiDto[]>(patientsQueryKey) ?? [];
      const currentPatient = queryClient.getQueryData<PatientApiDto>(patientQueryKey)!;
      const updatedPatients = currentPatients.map((patient) =>
        patient.id === patientId && clinicalNote.actionType === "review"
          ? resetUnreviewedReadingCounts(patient, clinicalNote.type)
          : patient
      );
      const updatedCurrentPatient = resetUnreviewedReadingCounts(currentPatient, clinicalNote.type);
      queryClient.setQueryData<PatientApiDto[]>(patientsQueryKey, updatedPatients);
      queryClient.setQueryData<PatientApiDto>(patientQueryKey, updatedCurrentPatient);
      return () => {
        queryClient.setQueryData<PatientApiDto[]>(patientsQueryKey, currentPatients);
        queryClient.setQueryData<PatientApiDto>(patientQueryKey, currentPatient);
      };
    },
    onSuccess: async (_data, clinicalNote) => {
      await queryClient.cancelQueries({ queryKey: clinicalNotesQueryKey });

      let user: CaregiverApiDto | null = null;
      try {
        user = await ensureSelfUser(queryClient);
      } catch (error) {
        console.error(error);
      }

      const currentClinicalNotes =
        queryClient.getQueryData<ClinicalNote[]>(clinicalNotesQueryKey) ?? [];

      const now = Instant.now();
      const randomishPlaceholderId = `${patientId}-${now.epochSecond()}`;
      const updatedClinicalNotes = currentClinicalNotes.concat([
        {
          ...clinicalNote,
          id: randomishPlaceholderId,
          patientId,
          creatorName: user ? (user.displayName ?? `${user.firstName} ${user.lastName}`) : "",
          dateTime: now,
        },
      ]);
      queryClient.setQueryData<ClinicalNote[]>(clinicalNotesQueryKey, updatedClinicalNotes);
    },
    onError: (err, _value, rollback) => {
      console.error(err);
      if (rollback) {
        rollback();
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: patientsQueryKey });
      queryClient.invalidateQueries({ queryKey: clinicalNotesQueryKey });
    },
  });
};

type UpdateBloodPressureThresholdsMutationOptions = {
  patientId: string;
};

type UpdateBloodGlucoseThresholdsMutationOptions = {
  patientId: string;
};

export const useUpdateBloodPressureClinicalProtocolMutation = ({
  patientId,
}: UpdateBloodPressureThresholdsMutationOptions) => {
  const queryClient = useQueryClient();
  const patientQueryKey = ["patients", patientId];
  return useMutation({
    mutationFn: updateBloodPressureClinicalProtocol,
    onMutate: async ({ thresholds }) => {
      await queryClient.cancelQueries({ queryKey: patientQueryKey });
      let user: CaregiverApiDto | null = null;
      try {
        user = await ensureSelfUser(queryClient);
      } catch (error) {
        console.error(error);
      }
      const currentPatient = queryClient.getQueryData<PatientApiDto>(patientQueryKey)!;
      const updatedPatient = {
        ...currentPatient,
        hypertension: {
          ...currentPatient.hypertension!,
          thresholds: {
            ...thresholds,
            lastUpdatedAt: Instant.now().toString(),
            lastUpdatedBy: {
              id: user?.id ?? "",
              firstName: user?.firstName ?? "",
              lastName: user?.lastName ?? "",
              displayName: user ? (user.displayName ?? `${user.firstName} ${user.lastName}`) : "",
            },
          },
        },
      };
      queryClient.setQueryData<PatientApiDto>(patientQueryKey, updatedPatient);
      return () => {
        queryClient.setQueryData<PatientApiDto>(patientQueryKey, currentPatient);
      };
    },
    onError: (err, _value, rollback) => {
      console.error(err);
      if (rollback) {
        rollback();
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
    },
  });
};

export const useUpdateBloodGlucoseClinicalProtocolAndThresholdsMutation = ({
  patientId,
}: UpdateBloodGlucoseThresholdsMutationOptions) => {
  const queryClient = useQueryClient();
  const patientQueryKey = ["patients", patientId];
  return useMutation({
    mutationFn: updateBloodGlucoseClinicalProtocolAndThresholds,
    onMutate: async ({ thresholds }) => {
      await queryClient.cancelQueries({ queryKey: patientQueryKey });
      let user: CaregiverApiDto | null = null;
      try {
        user = await ensureSelfUser(queryClient);
      } catch (error) {
        console.error(error);
      }
      const currentPatient = queryClient.getQueryData<PatientApiDto>(patientQueryKey)!;
      const updatedPatient = {
        ...currentPatient,
        diabetes: {
          ...currentPatient.diabetes!,
          thresholds: {
            ...thresholds,
            lastUpdatedAt: Instant.now().toString(),
            lastUpdatedBy: {
              id: user?.id ?? "",
              firstName: user?.firstName ?? "",
              lastName: user?.lastName ?? "",
              displayName: user ? (user.displayName ?? `${user.firstName} ${user.lastName}`) : "",
            },
          },
        },
      };
      queryClient.setQueryData<PatientApiDto>(patientQueryKey, updatedPatient);
      // TODO: Also update query data for the patient in the patients query
      return () => {
        queryClient.setQueryData<PatientApiDto>(patientQueryKey, currentPatient);
      };
    },
    onError: (err, _value, rollback) => {
      console.error(err);
      if (rollback) {
        rollback();
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
    },
  });
};

const resetUnreviewedReadingCounts = (
  patient: PatientApiDto,
  protocolType: ClinicalProtocolType
): PatientApiDto => ({
  ...patient,
  hypertension:
    protocolType === "hypertension"
      ? resetUnreviewedHypertensionReadingCounts(patient.hypertension!)
      : patient.hypertension,
  diabetes:
    protocolType === "diabetes"
      ? resetUnreviewedDiabetesReadingCounts(patient.diabetes!)
      : patient.diabetes,
});

const resetUnreviewedHypertensionReadingCounts = (
  hypertension: HypertensionFieldsApiDto
): HypertensionFieldsApiDto => ({
  ...hypertension,
  unreviewedReadings: { total: 0, low: 0, elevated: 0, acceptable: 0, critical: 0 },
});

const resetUnreviewedDiabetesReadingCounts = (
  diabetes: DiabetesFieldsApiDto
): DiabetesFieldsApiDto => ({
  ...diabetes,
  unreviewedReadings: {
    total: 0,
    emergencyLow: 0,
    criticalLow: 0,
    low: 0,
    elevated: 0,
    acceptable: 0,
    criticalHigh: 0,
    pending: 0,
    unclassified: 0,
  },
});

export const useOffboardPatientMutation = ({ patientId }: { patientId: string }) => {
  const queryClient = useQueryClient();
  const patientsQueryKey = ["patients"];
  const patientQueryKey = ["patients", patientId];
  return useMutation({
    mutationFn: async ({ prescriptionId, reason, deliveryDate, note }: OffboardingRequest) =>
      await offboardPatient({
        prescriptionId,
        reason,
        deliveryDate: deliveryDate?.toString(),
        note,
      }),
    onMutate: async ({ deliveryDate }) => {
      await queryClient.cancelQueries({ queryKey: patientQueryKey });
      await queryClient.cancelQueries({ queryKey: patientsQueryKey });
      const currentPatient = queryClient.getQueryData<PatientApiDto>(patientQueryKey);
      const currentPatients = queryClient.getQueryData<PatientApiDto[]>(patientsQueryKey);
      if (!currentPatient) {
        return;
      }
      const update = {
        deliveryDate: deliveryDate?.toString(),
        status: "offboarded" as const,
      };
      const updatedPatient = {
        ...currentPatient,
        hypertension: updateIfDefined(currentPatient.hypertension, update),
        diabetes: updateIfDefined(currentPatient.diabetes, update),
      };
      const updatedPatients = currentPatients?.map((patient) =>
        patient.id === patientId ? updatedPatient : patient
      );
      queryClient.setQueryData<PatientApiDto>(patientQueryKey, updatedPatient);
      queryClient.setQueryData<PatientApiDto[]>(patientsQueryKey, updatedPatients);
      return () => {
        queryClient.setQueryData<PatientApiDto>(patientQueryKey, currentPatient);
        queryClient.setQueryData<PatientApiDto[]>(patientsQueryKey, currentPatients);
      };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: patientsQueryKey });
    },
    onError: (err, _value, rollback) => {
      console.error(err);
      if (rollback) {
        rollback();
      }
    },
  });
};

export const useUpdateDeliveryDateMutation = () => {
  const queryClient = useQueryClient();
  const patientsQueryKey = ["patients"];
  return useMutation({
    mutationFn: async ({
      patientId,
      deliveryDate,
    }: {
      patientId: string;
      deliveryDate: LocalDate | undefined;
    }) => {
      await updateDeliveryDate({ patientId, deliveryDate: deliveryDate?.toString() });
    },
    onMutate: async ({ patientId, deliveryDate }) => {
      const patientQueryKey = ["patients", patientId];
      await queryClient.cancelQueries({ queryKey: patientQueryKey });
      await queryClient.cancelQueries({ queryKey: patientsQueryKey });
      const currentPatient = queryClient.getQueryData<PatientApiDto>(patientQueryKey);
      const currentPatients = queryClient.getQueryData<PatientApiDto[]>(patientsQueryKey);
      if (!currentPatient) {
        return;
      }
      const { hypertension, diabetes } = currentPatient;
      const updatedPatient = {
        ...currentPatient,
        hypertension: hypertension && {
          ...hypertension,
          deliveryDate: deliveryDate?.toString(),
          status: updatedStatusFor(hypertension, deliveryDate),
        },
        diabetes: diabetes && {
          ...diabetes,
          deliveryDate: deliveryDate?.toString(),
          status: updatedStatusFor(diabetes, deliveryDate),
        },
      };
      const updatedPatients = currentPatients?.map((patient) =>
        patient.id === patientId ? updatedPatient : patient
      );
      queryClient.setQueryData<PatientApiDto>(patientQueryKey, updatedPatient);
      queryClient.setQueryData<PatientApiDto[]>(patientsQueryKey, updatedPatients);
      return () => {
        queryClient.setQueryData<PatientApiDto>(patientQueryKey, currentPatient);
        queryClient.setQueryData<PatientApiDto[]>(patientsQueryKey, currentPatients);
      };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: patientsQueryKey });
    },
    onError: (err, _value, rollback) => {
      console.error(err);
      if (rollback) {
        rollback();
      }
    },
  });
};

const updatedStatusFor = (
  prescription: HypertensionFieldsApiDto | DiabetesFieldsApiDto,
  deliveryDate: LocalDate | undefined
) => {
  const status = prescription.status;
  const isMonitoring = status === "monitoring" || status === "postpartum-monitoring";
  return !isMonitoring
    ? status
    : deliveryDate
      ? ("postpartum-monitoring" as const)
      : ("monitoring" as const);
};

export const useOrganizationQuery = () => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["organization"],
    queryFn: async () => {
      const { organizationId } = await ensureSelfUser(queryClient);
      return await getOrganization(organizationId);
    },
    select: toOrganization,
    refetchOnWindowFocus: true,
  });
};
